import type { FC } from 'react';
import dp8 from './../../../resources/content/dp8.png';
import { KnapsackWrapper } from './01Knapsack.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface KnapsackProps {}

const Knapsack: FC<KnapsackProps> = () => (
 <KnapsackWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">0/1 Knapsack</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-center">The knapsack problem is a classic optimization challenge where you need to select items to pack in a knapsack with limited capacity. </p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="400" src={dp8} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <ul className="text-align-justify">
        <li>You must take the entire item or leave it completely.</li>
        <li>No fractional parts allowed.</li>
        <li>Goal is to Maximize Total Value *Avoiding Exceeding Capacity*</li>
        <li>You must make a binary choice ~ include it or exclude it. You cannot partially include an item, which is what makes this the "0/1" variant.</li>
      </ul>
      <br/>
      <h4 className="text-align-left">Classic 0/1 Knapsack</h4>
      <ul className="text-align-justify">
        <li>A bag (knapsack) contains several items, each with:</li>
        <ul className="text-align-justify">
          <li>a weight</li>
          <li>a value (profit)</li>
        </ul>
        <li>Our goal is to pick items so that total weight ≤ limit and total value is as large as possible. </li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Total Knapsack Capacity w = 5\n'+
            '	~ Item 1: weight = 2kg, value = 3\n'+
            '	~ Item 2: weight = 3kg, value = 4\n'+
            '\n'+
            'If you take the 3 kg item: \n'+
            '	Remaining = 5-3 → 2kg\n'+
            '\n'+
            'Value for 3kg → 4kg\n'+
            'Value for 2kg → 3kg\n'+
            '	\n'+
            '👉 Total = 4 + 3 → 7kg'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Subset Sum</h4>
      <ul className="text-align-justify">
        <li>The problem has two possible outcomes:</li>
        <ul className="text-align-justify">
          <li>True: A subset exists that sums to our target</li>
          <li>False: No such subset exists</li>
        </ul>
        <li>The goal is to determine whether there exists a subset of the given set with a sum equal to the target.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [2, 3, 7] and target = 5\n'+
            '\n'+
            'Possible options:\n'+
            '	→ 2+3 : 5\n'+
            '	→ 3+7 : 10\n'+
            '	→ 2+3+7 : 15\n'+
            '	\n'+
            '👉 Answer is yes as 2+3 gives us 5'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Partition Equal Subset Sum</h4>
      <ul className="text-align-justify">
        <li>Check if an array can be divided into two equal-sum subsets.</li>
		    <li>If total sum = S, then each group must have: S/2</li>
		    <li>So the problem becomes: Can we find a subset with sum = S/2 ?</li>
	      <li>But we have one edge case here:</li>
        <ul className="text-align-justify">
          <li>If total sum is odd: S%2 != 0</li>
		      <li>Answer = FALSE (because you can’t split it into equal halves)</li>
        </ul>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [1, 5, 11, 5]\n'+
            '		\n'+
            '22 is even → possible\n'+
            'Each subset must have sum: 22 / 2 = 11\n'+
            'Possible subsets:\n'+
            '	{11}\n'+
            '	{5, 5, 1}\n'+
            '	\n'+
            '👉  Answer is Yes'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Target Sum</h4>
      <ul className="text-align-justify">
        <li>Count the number of ways to achieve a target sum.</li>
        <li>In Target Sum we actually count how many ways.</li>
        <li>Think of it like : “Every way to make (i − num) becomes a new way to make i by adding num.”</li>
        <li>The goal is to determine how many ways we can assign + or - signs to each element so that the resulting expression equals the target.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Numbers = [1, 2, 3] and Target = 4\n'+
            '\n'+
            'Here 1+3 can give us 4. \n'+
            'So, 1 way to achieve it.\n'+
            '\n'+
            '👉 Answer is 1'
          }
        </SyntaxHighlighter>
      </ul>

   </div>
 </KnapsackWrapper>
);

export default Knapsack;
